import { Appointment } from "components/rep/accounts/cardNewDesign/types";
import moment from "moment";
import toast from "react-hot-toast";
import Holidays from "date-holidays";
import { PLANS_DATA } from "components/payments/constants";

const DATE_FORMAT: string = "M/D/YY";
const APPOINTMENT_DAY_FORMAT: string = "MMM Do, YYYY";
const TIME_FORMAT: string = "h:mma";
const WEEK_DAY: string = "dddd";
const REFERRAL_URL = {
  development: "http://localhost:3000/client?referral=",
  staging: "https://stage.myrepapp.com/client?referral=",
  production: "https://myrepapp.com/client?referral=",
};
interface iNotificationType {
  label?: string;
  emailSubtext?: string;
  smsSubtext?: string;
  emailProp?: string;
  smsProp?: string;
  allowedRoles?: string[];
}
interface iReferralUrls {
  development: string;
  staging: string;
  production: string;
}
const ROLE_NAMES = {
  BASIC: "Basic",
  PLUS: "Plus",
  PREMIER: "Premier",
  ADMIN: "Admin",
  CLIENT: "Client",
  DELEGATED_USER: "Delegated User",
  OFFICE_KIOSK: "Office Kiosk",
};

const NOTIFICATION_TYPES: iNotificationType[] = [
  {
    label: "Appointment Reminder",
    emailSubtext: "Receive an email a day before the appointment.",
    smsSubtext:
      "Receive a text message on your phone a day before the appointment.",
    emailProp: "aptReminderEmail",
    smsProp: "aptReminderSms",
    allowedRoles: [ROLE_NAMES.BASIC, ROLE_NAMES.PLUS, ROLE_NAMES.PREMIER],
  },
  {
    label: "Appointment Cancellation",
    emailSubtext:
      "Receive an email if your appointment is cancelled by the office.",
    smsSubtext:
      "Receive a text message on your phone if your appointment is cancelled by the office.",
    emailProp: "aptCancellationEmail",
    smsProp: "aptCancellationSms",
    allowedRoles: [ROLE_NAMES.BASIC, ROLE_NAMES.PLUS, ROLE_NAMES.PREMIER],
  },
  {
    label: "Appointment Opening",
    emailSubtext:
      "Receive an email when new appointment is available for the office you follow.",
    smsSubtext:
      "Receive a text message on your phone when new appointment is available for the office you follow.",
    emailProp: "aptOpeningEmail",
    smsProp: "aptOpeningSms",
    allowedRoles: [ROLE_NAMES.PLUS, ROLE_NAMES.PREMIER],
  },
  {
    label: "Appointment Transfer",
    emailSubtext:
      "Receive an email on when you receive a transfer of an appointment from another premier user.",
    smsSubtext:
      "Receive a text message on your phone when you receive a transfer of an appointment from another premier user.",
    emailProp: "aptTransferEmail",
    smsProp: "aptTransferSms",
    allowedRoles: [ROLE_NAMES.PREMIER],
  },
  {
    label: "Direct Messages from Offices",
    smsSubtext:
      " Receive a text message on your phone when an office sends you a direct message via email.",
    smsProp: "ofcMessageSms",
    allowedRoles: [ROLE_NAMES.BASIC, ROLE_NAMES.PLUS, ROLE_NAMES.PREMIER],
  },
];
const getDisplayDate = (date: string) => {
  return moment(date).format("M/D/YY");
};

const makeToast = (message: string, type: string, time: number = 3000) => {
  const t =
    type === "success" ? toast.success : type === "error" ? toast.error : toast;
  t(message, {
    duration: time,
    position: "top-center",
  });
};
const sortAppointments = (appointments: Appointment[]) => {
  return [...appointments].sort((a, b) => {
    const dateA = moment(a.date, "YYYY-MM-DD");
    const dateB = moment(b.date, "YYYY-MM-DD");

    return dateA.diff(dateB);
  });
};
const formatTime = (appointmentTimeInSeconds: number) => {
  const hours = Math.floor(appointmentTimeInSeconds / 3600) % 12;
  const minutes = Math.floor((appointmentTimeInSeconds % 3600) / 60);
  const isAM = Math.floor(appointmentTimeInSeconds / 3600) < 12;

  const formattedHours = hours === 0 ? 12 : hours;

  const formattedMinutes = String(minutes).padStart(2, "0");

  const ampm = isAM ? "AM" : "PM";

  return `${formattedHours}:${formattedMinutes} ${ampm}`;
};
const getRepeatingDates = (
  startDateStr: string,
  endDateStr: string,
  repeatingDays: number[],
  repeatYearly: boolean = false,
): string[] => {
  const dates = [];
  let startDate, endDate;
  const today = moment().startOf("day");
  if (repeatYearly) {
    startDate = today;
    endDate = moment().endOf("year");
  } else {
    startDate = moment(startDateStr, "MM/DD/YYYY");
    endDate = moment(endDateStr, "MM/DD/YYYY");
    if (startDate.isBefore(today)) {
      startDate = today;
    }
  }

  while (startDate.isSameOrBefore(endDate)) {
    if (repeatingDays.includes(startDate.day())) {
      dates.push(startDate.format("YYYY-MM-DD"));
    }
    startDate.add(1, "day");
  }

  return dates;
};
const convertToDateTimeSlots = (
  templateDates: string[],
  templateTimes: number[],
): string[] => {
  return templateDates.flatMap((date) =>
    templateTimes.map((timeInSeconds) => {
      const hours = Math.floor(timeInSeconds / 3600);
      const minutes = (timeInSeconds % 3600) / 60;
      const dateObj = new Date(
        `${date}T${String(hours).padStart(2, "0")}:${String(minutes).padStart(
          2,
          "0",
        )}:00.000Z`,
      );
      return dateObj.toISOString();
    }),
  );
};

const processTimestamps = (timestamps: string[]) => {
  const dates = timestamps.map((date) => new Date(date));

  const startDate = moment(
    new Date(Math.min(...dates.map((d) => d.getTime()))),
  ).format("MM/DD/YYYY");
  const endDate = moment(
    new Date(Math.max(...dates.map((d) => d.getTime()))),
  ).format("MM/DD/YYYY");
  const timeSet = new Set(dates.map((d) => moment.utc(d).format("h:mmA")));
  const times = Array.from(timeSet)
    .sort((a, b) => {
      const dateA = moment(`2000-01-01 ${a}`, "YYYY-MM-DD h:mmA");
      const dateB = moment(`2000-01-01 ${b}`, "YYYY-MM-DD h:mmA");
      return dateA.valueOf() - dateB.valueOf();
    })
    .map((time: string) => {
      const momentTime = moment(time, "h:mmA");
      return momentTime.hours() * 60 + momentTime.minutes();
    });

  return {
    startDate,
    endDate,
    times,
  };
};
const isArrayEqual = (arr1: number[], arr2: number[]) => {
  if (arr1.length !== arr2.length) return false;
  return arr1.every((value, index) => value === arr2[index]);
};
const getAddedAndRemovedDates = (
  newDateTimeSlots: string[],
  oldDateTimeSlots: string[],
) => {
  const newSet = new Set(newDateTimeSlots);
  const oldSet = new Set(oldDateTimeSlots);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const addedDates = newDateTimeSlots.filter((date) => !oldSet.has(date));
  const removedDates = oldDateTimeSlots.filter(
    (date) => !newSet.has(date) && new Date(date) >= today,
  );

  return { addedDates, removedDates };
};
const HOME_SECTIONS = {
  benefits: "benefitsRef",
  howItWorks: "howItWorksRef",
  testimonial: "testimonialsRef",
  faq: "faqRef",
};

const getHolidays = () => {
  const hd = new Holidays("US");
  const holidays = [
    ...hd.getHolidays(moment().subtract(1, "year").format("YYYY")),
    ...hd.getHolidays(moment().format("YYYY")),
    ...hd.getHolidays(moment().add(1, "year").format("YYYY")),
  ].map((value) => {
    const data = {
      ...value,
      date: value.date.substring(0, 10),
      title: value.name,
      start: value.date.substring(0, 10),
      type: value.type,
    };

    return data;
  });
  return holidays;
};

const getUserReferralLink = (userId: number) => {
  return `${
    REFERRAL_URL[process.env.NODE_ENV as keyof iReferralUrls]
  }${userId}`;
};
const annotationColorParser = (obj: {
  bg?: string;
  text?: string;
  border?: string;
}): string => {
  return `bg=${obj?.bg || "#fffff"}&&text=${obj?.text || "#2d4484"}&&border=${
    obj?.border || "#bcbcbc"
  }`;
};

const annotationColorDeparser = (
  str: string,
): { bg: string; text: string; border: string } => {
  const parts = str.split("&&");
  const result: { [key: string]: string } = {};
  parts.forEach((part) => {
    const [key, value] = part.split("=");
    result[key.trim()] = value ? value.trim() : "";
  });
  return {
    bg: result.bg || "",
    text: result.text || "",
    border: result.border || "",
  };
};
const getDynamicPlans = (config: JObj[]) => {
  const configMap = Object.fromEntries(config.map((c) => [c.key, c.value]));

  return PLANS_DATA.map((plan) => {
    if (plan.planId === 4) {
      // Plus plan
      return {
        ...plan,
        monthlyPrice: parseFloat(
          configMap["price_plus_monthly"] ?? plan.monthlyPrice,
        ),
        yearlyPrice: parseFloat(
          configMap["price_plus_annual"] ?? plan.yearlyPrice,
        ),
      };
    }

    if (plan.planId === 5) {
      // Premier plan
      return {
        ...plan,
        monthlyPrice: parseFloat(
          configMap["price_premier_monthly"] ?? plan.monthlyPrice,
        ),
        yearlyPrice: parseFloat(
          configMap["price_premier_annual"] ?? plan.yearlyPrice,
        ),
      };
    }

    return plan;
  });
};
const findCurrentAndNextReleaseDates = (
  releaseDates: string[],
  now = moment(),
  includeToday = false,
): {
  currentReleaseDate: moment.Moment | null;
  nextReleaseDate: moment.Moment | null;
} => {
  // Sort release dates to ensure they're in chronological order
  releaseDates.sort((a, b) => moment(a).diff(moment(b)));

  let currentReleaseDate = null;
  let nextReleaseDate = null;

  // Find the current and next release dates
  for (let i = 0; i < releaseDates.length; i++) {
    const releaseDate = moment(releaseDates[i]);
    const check = includeToday
      ? now.isSameOrAfter(releaseDate, "day")
      : now.isAfter(releaseDate, "day");
    if (check) {
      currentReleaseDate = releaseDate;
      nextReleaseDate =
        i < releaseDates.length - 1 ? moment(releaseDates[i + 1]) : null;
    } else if (!currentReleaseDate) {
      // If we haven't found a current release date yet, this is the next one
      nextReleaseDate = releaseDate;
      break;
    }
  }

  // If we don't have a current release date, use the first one in the list
  if (!currentReleaseDate && releaseDates.length > 0) {
    currentReleaseDate = moment(releaseDates[0]);
  }

  return { currentReleaseDate, nextReleaseDate };
};
const getCurrentPeriod = (dates: string[]) => {
  const now = new Date();
  let prev = null,
    next = null,
    current = null,
    idx = -1;

  // Parse date strings as local midnight to avoid UTC-offset shifting.
  // new Date("YYYY-MM-DD") parses as UTC midnight, which in negative-offset
  // timezones (e.g. LA, UTC-7) shifts the date back one day visually.
  const parseLocalDate = (dateStr: string): Date => {
    const [year, month, day] = dateStr.split("-").map(Number);
    return new Date(year, month - 1, day);
  };

  for (let i = 0; i < dates.length; i++) {
    const releaseDate = parseLocalDate(dates[i]);
    if (releaseDate > now) {
      next = releaseDate;
      prev = i > 0 ? parseLocalDate(dates[i - 1]) : null;
      current = prev
        ? {
            start: prev,
            // Use `next` to derive the end: the current window covers through
            // the last day of the month the next release falls in.
            // Falling back to prev + 3 months (day-0 trick) when there is no
            // next release keeps backward-compat for the tail of the date list.
            end: next
              ? new Date(next.getFullYear(), next.getMonth() + 1, 0)
              : new Date(prev.getFullYear(), prev.getMonth() + 3, 0),
          }
        : null;
      idx = i;
      break;
    }
  }
  return { prev, current, next, idx };
};
const parseReleaseDates = (config: JObj[]) => {
  const releaseConfig = config.find((c) => c.key === "ALL_RELEASE_DATES");
  if (!releaseConfig) return [];
  try {
    return JSON.parse(releaseConfig.value);
  } catch {
    return [];
  }
};
export {
  DATE_FORMAT,
  WEEK_DAY,
  getDisplayDate,
  TIME_FORMAT,
  makeToast,
  APPOINTMENT_DAY_FORMAT,
  HOME_SECTIONS,
  sortAppointments,
  formatTime,
  ROLE_NAMES,
  NOTIFICATION_TYPES,
  iNotificationType,
  getRepeatingDates,
  convertToDateTimeSlots,
  processTimestamps,
  isArrayEqual,
  getAddedAndRemovedDates,
  getHolidays,
  REFERRAL_URL,
  iReferralUrls,
  getUserReferralLink,
  annotationColorParser,
  annotationColorDeparser,
  getDynamicPlans,
  findCurrentAndNextReleaseDates,
  getCurrentPeriod,
  parseReleaseDates,
};
